import type { ParentTicketFormValues, TicketPartFormRow } from '@/types/ticket'
import { hasFiniteCoords } from '@/components/forms/parentTicket/dispatchSitePin'

export type ParentTicketSubmitValidationOptions = {
  /** True when save will apply dispatch (submit, or edit of already-submitted ticket). */
  applyDispatch?: boolean
  requiresDispatch?: boolean
  dispatchReadiness?: 'pending' | 'on_hold' | 'ready'
  hasDispatchSelection?: boolean
  /** Deferred dispatch: site pin must be confirmed before Ready submit. */
  sitePinConfirmed?: boolean
  /** Finite lat/lng on the effective dispatch location (store or free-text). */
  hasDispatchCoords?: boolean
}

/** Normalize part name for duplicate comparison (trim, collapse spaces, case-insensitive). */
export function normalizePartNameKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * Indexes of rows that share a duplicate part name (or catalog id) with another row.
 * Empty names are ignored.
 */
export function getDuplicatePartIndexes(parts: TicketPartFormRow[] | undefined): Set<number> {
  const rows = parts ?? []
  const byName = new Map<string, number>()
  const byCatalog = new Map<number, number>()
  const dupes = new Set<number>()

  rows.forEach((part, index) => {
    const catalogId = part.catalogId != null && Number(part.catalogId) > 0 ? Number(part.catalogId) : null
    if (catalogId != null) {
      const prev = byCatalog.get(catalogId)
      if (prev !== undefined) {
        dupes.add(prev)
        dupes.add(index)
      } else {
        byCatalog.set(catalogId, index)
      }
    }

    const key = normalizePartNameKey(part.part_name ?? '')
    if (!key) return
    const prev = byName.get(key)
    if (prev !== undefined) {
      dupes.add(prev)
      dupes.add(index)
    } else {
      byName.set(key, index)
    }
  })

  return dupes
}

/**
 * Core + Ready-dispatch submit gates for ParentTicketForm.
 * Returns false and calls showError when validation fails.
 */
export function validateParentTicketSubmit(
  form: ParentTicketFormValues,
  showError: (message: string) => void,
  options?: ParentTicketSubmitValidationOptions,
): boolean {
  const missingFields: string[] = []
  if (!form.requestId) missingFields.push('Request')
  if (!form.agentId) missingFields.push('Agent')
  if (!(form.subject ?? '').trim()) missingFields.push('Subject')
  if (!(form.severity ?? '').trim()) missingFields.push('Severity')
  if (!(form.source ?? '').trim()) missingFields.push('Source')
  if (!(form.issueDescription ?? '').trim()) missingFields.push('Description')

  if (missingFields.length > 0) {
    showError(`Please fill in required fields: ${missingFields.join(', ')}`)
    return false
  }

  if (form.hasPartsNeeded) {
    const namedParts = (form.parts ?? []).filter((p) => normalizePartNameKey(p.part_name ?? '') !== '')
    if (namedParts.length === 0) {
      showError('Add at least one tool/part, or uncheck Tools / parts needed.')
      return false
    }
    if (getDuplicatePartIndexes(form.parts).size > 0) {
      showError('Duplicate tools/parts are not allowed. Each item can only be listed once.')
      return false
    }
  }

  // Align with BE: brand required on any apply/submit when certification is checked.
  if (
    options?.applyDispatch &&
    form.certificationRequired &&
    (form.brandId == null || form.brandId === undefined)
  ) {
    showError('Please select a Brand when Certification Required is checked')
    return false
  }

  const readyDispatch =
    Boolean(options?.applyDispatch) &&
    Boolean(options?.requiresDispatch) &&
    options?.dispatchReadiness === 'ready'

  if (!readyDispatch) return true

  if (!options?.sitePinConfirmed) {
    showError('Confirm the site pin in the field engineer dispatch section before submitting.')
    return false
  }

  if (!options?.hasDispatchCoords) {
    showError('Select a store or location with coordinates in the field engineer dispatch section.')
    return false
  }

  if (!options?.hasDispatchSelection) {
    showError('Assign a primary field engineer before submitting with Ready dispatch.')
    return false
  }

  return true
}

/** Whether Ready+dispatch Submit should be enabled (button disable helper). */
export function isReadyDispatchSubmitAllowed(options: {
  requiresDispatch: boolean
  dispatchReadiness: 'pending' | 'on_hold' | 'ready'
  sitePinConfirmed: boolean
  hasDispatchSelection: boolean
  latitude?: number | null
  longitude?: number | null
  hasUnresolvedInvalidDispatch?: boolean
}): boolean {
  if (!options.requiresDispatch || options.dispatchReadiness !== 'ready') return true
  if (options.hasUnresolvedInvalidDispatch) return false
  if (!options.sitePinConfirmed) return false
  if (!options.hasDispatchSelection) return false
  if (!hasFiniteCoords(options.latitude, options.longitude)) return false
  return true
}

/** Context-rail checklist: FE item complete for current readiness. */
export function isDispatchChecklistComplete(options: {
  requiresDispatch: boolean
  dispatchReadiness?: 'pending' | 'on_hold' | 'ready'
  hasDispatchSelection: boolean
  sitePinConfirmed?: boolean
  latitude?: number | null
  longitude?: number | null
}): boolean {
  if (!options.requiresDispatch) return true
  const readiness = options.dispatchReadiness ?? 'pending'
  if (readiness === 'pending' || readiness === 'on_hold') return true
  return (
    Boolean(options.hasDispatchSelection) &&
    Boolean(options.sitePinConfirmed) &&
    hasFiniteCoords(options.latitude, options.longitude)
  )
}
